/**
 * Merkez + cihaz fiyat önbelleği sağlayıcısı. `repo/RepositoryContext.tsx`
 * deseninin aynısı, ama AYRI bir bağlam — sebebi:
 *
 * `RepositoryContext` Aşama 1'de donan `LocalRepository` sözleşmesini taşıyor.
 * Aşama 2'nin yeni yeteneği (önbellek + merkez) o sözleşmenin YANINDA duruyor
 * (`packages/local/src/browser/price-cache-api.ts` başındaki nota bak), o
 * yüzden arayüzde de yanında duruyor. Aşama kapısında ikisi sözleşmede
 * birleşirse bu bağlam da depo bağlamına katılabilir.
 *
 * SAĞLAYICI YOKSA ÇÖKMEZ: `useCentral()` merkezsiz bir varsayılan döndürür.
 * Bu, uçak modu kuralının arayüz tarafındaki karşılığı — merkezin yokluğu bir
 * ARIZA DEĞİL, beklenen bir durum; ekranlar onu görünür kılar, çökmez.
 */

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { CentralPort, PriceCachePort, SyncEngine } from "@parafon/contracts/device-api";
import { M } from "../dil";

export interface CentralContextValue {
  /** null → merkez yok; `unavailable` sebebi taşır. */
  readonly central: CentralPort | null;
  /** null → bu cihazda fiyat önbelleği yok; `cacheUnavailable` sebebi taşır. */
  readonly prices: PriceCachePort | null;
  readonly sync: SyncEngine | null;
  readonly isFake: boolean;
  readonly unavailable: string | null;
  readonly cacheUnavailable: string | null;
  /** Eşitleme sonrası listelerin yenilenmesi için. */
  readonly revision: number;
  readonly invalidate: () => void;
}

const NO_CENTRAL: CentralContextValue = {
  central: null,
  prices: null,
  sync: null,
  isFake: false,
  unavailable: M.kabuk.merkez.ekrandaYok,
  cacheUnavailable: M.kabuk.merkez.ekrandaOnbellekYok,
  revision: 0,
  invalidate: () => undefined,
};

const CentralContext = createContext<CentralContextValue | null>(null);

export function CentralProvider({
  value,
  children,
}: {
  readonly value: CentralContextValue;
  readonly children: ReactNode;
}) {
  const memo = useMemo(() => value, [value]);
  return <CentralContext.Provider value={memo}>{children}</CentralContext.Provider>;
}

export function useCentral(): CentralContextValue {
  return useContext(CentralContext) ?? NO_CENTRAL;
}
